import { useForm } from "@tanstack/react-form";
import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { useRef, useState } from "react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "../../components/ui/field";
import { MarkdownEditor } from "../../components/ui/markdown-editor";
import { MarkdownView } from "../../components/ui/markdown-view";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from "../../components/ui/sheet";
import { Switch } from "../../components/ui/switch";

export type MemberDetail = {
	membershipId: Id<"tenantMembers">;
	userId: Id<"users">;
	name: string;
	email: string;
	joinedAt: number;
	isStaff: boolean;
	signatureHtml: string | null;
	outlook: null | {
		connectionId: Id<"oauthConnections">;
		accountEmail?: string;
		status:
			| "connected"
			| "disconnected"
			| "error"
			| "needs_reauth"
			| "unsupported_mailbox";
		autoDraftEnabled: boolean;
	};
};

export default function MemberDetailSheet({
	tenantId,
	member,
	open,
	onOpenChange,
	onRequestRemove,
}: {
	tenantId: Id<"tenants">;
	member: MemberDetail | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onRequestRemove: (member: MemberDetail) => void;
}) {
	const setAutoDraft = useMutation(api.staff.setMemberOutlookAutoDraft);
	const setSignature = useMutation(api.staff.setMemberSignature);

	// Hold the last opened member so the sheet keeps rendering its content
	// while it animates closed.
	const lastMemberRef = useRef<MemberDetail | null>(member);
	if (member) lastMemberRef.current = member;
	const displayMember = member ?? lastMemberRef.current;
	const outlook = displayMember?.outlook ?? null;

	const initialAutoDraft = outlook?.autoDraftEnabled ?? false;
	const initialSignature = displayMember?.signatureHtml ?? "";

	const [signatureDraft, setSignatureDraft] = useState(initialSignature);
	const [signatureDirty, setSignatureDirty] = useState(false);

	// Reset when the member changes.
	const lastMemberId = useRef<Id<"tenantMembers"> | null>(null);
	const memberId = displayMember?.membershipId ?? null;
	if (memberId !== lastMemberId.current) {
		lastMemberId.current = memberId;
		setSignatureDraft(initialSignature);
		setSignatureDirty(false);
	} else if (!signatureDirty && signatureDraft !== initialSignature) {
		setSignatureDraft(initialSignature);
	}

	const {
		mutateAsync: saveAutoDraft,
		isPending: savingAutoDraft,
		error: autoDraftError,
	} = useReactQueryMutation({
		mutationFn: async (args: { userId: Id<"users">; enabled: boolean }) => {
			await setAutoDraft({
				tenantId,
				userId: args.userId,
				enabled: args.enabled,
			});
		},
	});

	const {
		mutate: saveSignature,
		isPending: savingSignature,
		error: signatureError,
	} = useReactQueryMutation({
		mutationFn: async () => {
			if (!displayMember) return;
			await setSignature({
				tenantId,
				userId: displayMember.userId,
				signatureHtml: signatureDraft,
			});
		},
		onSuccess: () => setSignatureDirty(false),
	});

	const form = useForm({
		defaultValues: {
			autoDraftEnabled: initialAutoDraft,
		},
		onSubmit: async ({ value }) => {
			if (!displayMember || !outlook) return;
			await saveAutoDraft({
				userId: displayMember.userId,
				enabled: value.autoDraftEnabled,
			});
		},
	});

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent
				side="right"
				className="sm:max-w-xl"
				key={displayMember?.membershipId ?? "empty"}
			>
				<SheetHeader>
					<SheetTitle className="flex items-center gap-2">
						{displayMember?.name ?? "Member"}
						{displayMember?.isStaff ? (
							<Badge variant="secondary">Staff</Badge>
						) : null}
					</SheetTitle>
					<SheetDescription>{displayMember?.email ?? ""}</SheetDescription>
				</SheetHeader>

				<ScrollArea className="min-h-0 flex-1">
					<div className="flex flex-col gap-6 px-4 pb-4">
						<form
							onSubmit={(e) => {
								e.preventDefault();
								form.handleSubmit();
							}}
						>
							<FieldGroup>
								{outlook ? (
									<form.Field name="autoDraftEnabled">
										{(field) => (
											<Field orientation="horizontal">
												<FieldContent>
													<FieldLabel htmlFor="auto-draft-switch">
														Auto-draft replies
													</FieldLabel>
													<FieldDescription>
														{outlook.accountEmail ?? "This mailbox"} —{" "}
														{outlook.status === "connected"
															? "when off, incoming emails won't trigger the Clerical agent."
															: `connection is ${outlook.status}.`}
													</FieldDescription>
												</FieldContent>
												<Switch
													id="auto-draft-switch"
													checked={field.state.value}
													disabled={savingAutoDraft}
													onCheckedChange={async (checked) => {
														field.handleChange(checked);
														if (!displayMember) return;
														await saveAutoDraft({
															userId: displayMember.userId,
															enabled: checked,
														});
													}}
												/>
											</Field>
										)}
									</form.Field>
								) : (
									<Field>
										<FieldLabel>Outlook mailbox</FieldLabel>
										<FieldDescription>
											No Outlook mailbox connected for this user.
										</FieldDescription>
									</Field>
								)}

								{autoDraftError ? (
									<FieldError>
										{autoDraftError instanceof Error
											? autoDraftError.message
											: String(autoDraftError)}
									</FieldError>
								) : null}
							</FieldGroup>
						</form>

						<div className="space-y-3">
							<div>
								<div className="text-sm font-medium">Email signature</div>
								<div className="text-xs text-muted-foreground">
									Appended to drafts created on this member's behalf. Paste or
									insert images directly into the editor.
								</div>
							</div>

							<MarkdownEditor
								format="html"
								value={signatureDraft}
								onChange={(v) => {
									setSignatureDraft(v);
									setSignatureDirty(true);
								}}
								placeholder="No signature set yet..."
							/>

							{signatureDraft.trim().length > 0 ? (
								<div>
									<div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
										Preview
									</div>
									<div className="rounded-md border bg-muted/30 p-3">
										<MarkdownView format="html" value={signatureDraft} />
									</div>
								</div>
							) : null}

							{signatureError ? (
								<FieldError>
									{signatureError instanceof Error
										? signatureError.message
										: String(signatureError)}
								</FieldError>
							) : null}

							<div className="flex items-center justify-end gap-2">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => {
										setSignatureDraft(displayMember?.signatureHtml ?? "");
										setSignatureDirty(false);
									}}
									disabled={savingSignature || !signatureDirty}
								>
									Revert
								</Button>
								<Button
									type="button"
									size="sm"
									onClick={() => saveSignature()}
									disabled={savingSignature || !signatureDirty}
								>
									{savingSignature ? "Saving..." : "Save signature"}
								</Button>
							</div>
						</div>
					</div>
				</ScrollArea>

				<SheetFooter className="justify-between gap-2 sm:justify-between">
					<Button
						type="button"
						variant="ghost"
						className="text-destructive hover:text-destructive"
						onClick={() => {
							if (displayMember) onRequestRemove(displayMember);
						}}
						disabled={!displayMember}
					>
						Remove from firm
					</Button>
					<Button
						type="button"
						variant="outline"
						onClick={() => onOpenChange(false)}
					>
						Close
					</Button>
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
}
